import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, 
  Save, 
  Database, 
  Shield, 
  AlertCircle, 
  Loader,
  CheckCircle,
  XCircle,
  Globe,
  Server,
  RefreshCw,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import configService from '../../services/configService';

const SystemConfiguration = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('system');

  // Query system and app configurations
  const { data: systemConfig, isLoading: systemLoading } = useQuery({
    queryKey: ['system-config'],
    queryFn: () => configService.getSystemConfig()
  });

  const { data: appConfig, isLoading: appLoading } = useQuery({
    queryKey: ['app-config'],  
    queryFn: () => configService.getAppConfig()
  });

  const { data: systemStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['system-status'],
    queryFn: () => configService.getSystemStatus(),
    refetchInterval: 30000
  });

  // Mutations
  const updateSystemMutation = useMutation({
    mutationFn: configService.updateSystemConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(['system-config']);
      toast.success('System configuration updated');
    },
    onError: (error) => toast.error(error.message)
  });

  const updateAppMutation = useMutation({
    mutationFn: configService.updateAppConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(['app-config']);
      toast.success('Application settings updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // State management
  const [systemSettings, setSystemSettings] = useState({});
  const [appSettings, setAppSettings] = useState({});
  const [errors, setErrors] = useState({});

  // Initialize state from API data
  useEffect(() => {
    if (systemConfig?.data) {
      setSystemSettings(systemConfig.data);
    }
  }, [systemConfig]);

  useEffect(() => {
    if (appConfig?.data) {
      setAppSettings(appConfig.data);
    }
  }, [appConfig]);

  const handleSaveSystem = () => {
    const validation = configService.validateSystemConfig(systemSettings);
    if (!validation.isValid) {
      setErrors(validation.errors);
      toast.error('Please fix validation errors');
      return;
    }
    setErrors({});
    updateSystemMutation.mutate(systemSettings);
  };

  const handleSaveApp = () => {
    updateAppMutation.mutate(appSettings);
  };

  const renderSystemStatus = () => {
    if (statusLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="ml-3">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-3 bg-gray-200 rounded w-16 mt-1"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (!systemStatus?.data) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <p className="ml-3 text-yellow-800 text-sm">Unable to load system status</p>
          </div>
        </div>
      );
    }

    const statusItems = configService.parseSystemStatus(systemStatus.data);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statusItems.map((item) => (
          <div key={item.component} className={`border rounded-lg p-4 ${
            item.healthy ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  item.healthy ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {item.healthy ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                </div>
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  item.healthy ? 'text-green-800' : 'text-red-800'
                }`}>
                  {item.component}
                </p>
                <p className={`text-sm ${
                  item.healthy ? 'text-green-600' : 'text-red-600'
                }`}>
                  {item.message}
                </p>
                {item.responseTime && (
                  <p className="text-xs text-gray-500 mt-1">
                    Response: {item.responseTime}ms
                  </p>
                )}
                {item.lastChecked && (
                  <p className="text-xs text-gray-500">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(item.lastChecked).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const isLoading = systemLoading || appLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  const tabs = [
    { id: 'system', label: 'System Settings', icon: Server },
    { id: 'application', label: 'Application Settings', icon: Globe },
    { id: 'status', label: 'System Status', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings className="w-6 h-6 mr-2" />
          System Configuration
        </h1>
        <p className="mt-2 text-gray-600">
          Manage maintenance mode, user registration, and monitor system health
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* System Settings Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">System Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Application Name
                  </label>
                  <input
                    type="text"
                    value={systemSettings.app_name || ''}
                    onChange={(e) => setSystemSettings({
                      ...systemSettings,
                      app_name: e.target.value
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.app_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter application name"
                  />
                  {errors.app_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.app_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Default Timezone
                  </label>
                  <select
                    value={systemSettings.timezone || ''}
                    onChange={(e) => setSystemSettings({
                      ...systemSettings,
                      timezone: e.target.value
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.timezone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select timezone</option>
                    {configService.getCommonTimezones().map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                  {errors.timezone && (
                    <p className="mt-1 text-sm text-red-600">{errors.timezone}</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="maintenance_mode"
                    checked={systemSettings.maintenance_mode || false}
                    onChange={(e) => setSystemSettings({
                      ...systemSettings,
                      maintenance_mode: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="maintenance_mode" className="ml-2 block text-sm text-gray-900">
                    Enable Maintenance Mode
                  </label>
                </div>

                {systemSettings.maintenance_mode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Maintenance Message
                    </label>
                    <textarea
                      value={systemSettings.maintenance_message || ''}
                      onChange={(e) => setSystemSettings({
                        ...systemSettings,
                        maintenance_message: e.target.value
                      })}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter maintenance message to display to users"
                    />
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="cache_enabled"
                    checked={systemSettings.cache_enabled || false}
                    onChange={(e) => setSystemSettings({
                      ...systemSettings,
                      cache_enabled: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="cache_enabled" className="ml-2 block text-sm text-gray-900">
                    Enable System Caching
                  </label>
                </div>
              </div>

              <button
                onClick={handleSaveSystem}
                disabled={updateSystemMutation.isLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {updateSystemMutation.isLoading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {updateSystemMutation.isLoading ? 'Saving...' : 'Save System Settings'}
              </button>
            </div>
          )}

          {/* Application Settings Tab */}
          {activeTab === 'application' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Application Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="registration_enabled"
                    checked={appSettings.registration_enabled || false}
                    onChange={(e) => setAppSettings({
                      ...appSettings,
                      registration_enabled: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="registration_enabled" className="ml-2 block text-sm text-gray-900">
                    Allow New User Registration
                  </label>
                </div>
                
                <p className="text-sm text-gray-500">
                  When enabled, new users can register for accounts. When disabled, only administrators can create new user accounts.
                </p>
              </div>

              <button
                onClick={handleSaveApp}
                disabled={updateAppMutation.isLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {updateAppMutation.isLoading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {updateAppMutation.isLoading ? 'Saving...' : 'Save Application Settings'}
              </button>
            </div>
          )}

          {/* System Status Tab */}
          {activeTab === 'status' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">System Health Status</h2>
                <button
                  onClick={() => queryClient.invalidateQueries(['system-status'])}
                  disabled={statusLoading}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50 flex items-center"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${statusLoading ? 'animate-spin' : ''}`} />
                  Refresh Status
                </button>
              </div>
              
              {renderSystemStatus()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemConfiguration;